import { createAction } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';

import { authentication } from '@perpay-web/authentication/singleton';
import {
    CARD_APPLICATION_STEPS,
    PERSONAL_DETAILS_STEP,
} from '@perpay-web/fintech/components/screens/CardApplication/CardApplicationSteps';
import { useActionObservableCallback } from '@perpay-web/hooks/useActionObservableCallback';
import { useSteps } from '@perpay-web/hooks/useSteps';
import { hookAction$ } from '@perpay-web/services/singletons';
import { createContextProvider } from '@perpay-web/utils/componentUtils';

/** @type {import('@reduxjs/toolkit').ActionCreatorWithOptionalPayload<string>} */
export const cardApplicationSetSteps = createAction(
    'CARD_APPLICATION_STEPS::SET_STEP',
);

/** @type {import('@reduxjs/toolkit').ActionCreatorWithPayload<void>} */
const cardApplicationStepsGoBack = createAction(
    'CARD_APPLICATION_STEPS::GO_BACK',
);

const useCardApplication = () => {
    const [showDetailedForm, setShowDetailedForm] = useState(false);
    const [showPhoneDisclosure, setShowPhoneDisclosure] = useState(false);
    const phoneNumber = authentication.getPhoneNumber();
    const authRefreshing = authentication.getIsRefreshing();
    const { setStep, ...steps } = useSteps(
        CARD_APPLICATION_STEPS,
        PERSONAL_DETAILS_STEP,
    );

    useEffect(() => {
        if (
            !authRefreshing &&
            !phoneNumber &&
            steps.step !== PERSONAL_DETAILS_STEP
        ) {
            setStep(PERSONAL_DETAILS_STEP);
        }
    }, [phoneNumber, setStep, steps.step, authRefreshing]);

    useActionObservableCallback(
        hookAction$,
        cardApplicationSetSteps().type,
        (step) => setStep(step),
    );

    useActionObservableCallback(
        hookAction$,
        cardApplicationStepsGoBack().type,
        () => steps.goBack(),
    );

    return {
        ...steps,
        setStep,
        showDetailedForm,
        setShowDetailedForm,
        showPhoneDisclosure,
        setShowPhoneDisclosure,
    };
};

const providerFactory = (Context) => {
    const Provider = ({ children }) => {
        const hook = useCardApplication();

        return <Context.Provider value={hook}>{children}</Context.Provider>;
    };

    return Provider;
};

export const [CardApplicationProvider, useCardApplicationContext] =
    createContextProvider(providerFactory);
